import React from "react";

import { toLower } from "lodash/fp";

import Typography from "@mui/material/Typography";

import CountChip from "@app/analytics/components/CountChip";
import SharedReportIcon from "@app/analytics/components/SharedReportIcon";
import { TableCell } from "@app/analytics/components/Table";
import {
  CHANNEL_OPTS_FOR_TOPIC_PROVIDER,
  MESSAGE_OPTS_FOR_TOPIC_PROVIDER,
} from "@app/analytics/constants/topics";
import useEditTopicReport from "@app/analytics/hooks/topics/useEditTopicReport";
import EditableReportTitle from "@app/analytics/MeetingReports/components/EditableReportTitle";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import type { Topic } from "@app/analytics/types";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import useMeasure from "@app/common/hooks/useMeasure";
import { toast } from "@app/common/hooks/useToasts";
import { ProductivityProviderId } from "@app/common/types/providerTypes";
import { pluralizePhrase } from "@app/common/utils";

import { Column, Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

const CELL_PADDING_PX = 16;

const LABEL_FOR_TOPIC_KIND: Record<Topic["kind"], string> = {
  email_folder: "Folder",
  email_label: "Label",
  external_domain: "Topic",
  semantic: "Topic",
};

const EditableTopicTitle = ({
  report,
  maxWidth,
}: {
  report: Topic;
  maxWidth: number;
}) => {
  const { editTopicReportTitle } = useEditTopicReport({
    context: "list",
  });

  const handleRenameReport = (title: string) => {
    editTopicReportTitle.mutate(
      { id: report.id, title },
      {
        onError: () => {
          toast.error(
            "Error updating report title. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  return (
    <EditableReportTitle
      report={report}
      maxWidth={maxWidth}
      onEditChange={handleRenameReport}
    />
  );
};

const TopicTitleCell = ({
  report,
  isEditable,
}: {
  report: Topic;
  isEditable: boolean;
}) => {
  const [cellRef, , { innerWidth: cellWidth }] = useMeasure();
  const [topicLabelRef, , { innerWidth: topicLabelWidth }] = useMeasure();

  const editReport = useReportsTable((state) =>
    isEditable ? state.editReport : null
  );

  const channelOpts = CHANNEL_OPTS_FOR_TOPIC_PROVIDER[report.provider];
  const messageOpts = MESSAGE_OPTS_FOR_TOPIC_PROVIDER[report.provider];

  const topicKindLabel = LABEL_FOR_TOPIC_KIND[report.kind];
  const sourceLabel =
    PRODUCTIVITY_PROVIDERS_BY_ID[report.provider as ProductivityProviderId]
      ?.displayNameShort;

  const isEditingReportTitle =
    editReport != null &&
    editReport.type === "topic" &&
    editReport.id === report.id;

  const showTooltip =
    Boolean(topicKindLabel && sourceLabel) && !isEditingReportTitle;

  return (
    <TableCell ref={cellRef} sx={{ maxWidth: "300px" }}>
      <Column spacing={0.5} overflow="hidden">
        <TooltipWrapper
          title={`This Readout was automatically generated from a ${sourceLabel} ${toLower(
            topicKindLabel
          )}`}
          show={showTooltip}
          placement="bottom-end"
          enterDelay={2000}
        >
          <Row variant="left">
            <Typography
              variant="bodySecondaryMed"
              color="shades.neutral.500"
              ref={topicLabelRef}
            >
              {topicKindLabel ?? "Readout"}:&nbsp;
            </Typography>
            {isEditingReportTitle ? (
              <EditableTopicTitle
                report={report}
                maxWidth={cellWidth - topicLabelWidth - CELL_PADDING_PX}
              />
            ) : (
              <Typography
                variant="bodySecondaryMed"
                color="shades.neutral.800"
                noWrap
              >
                {report.title}
              </Typography>
            )}
          </Row>
        </TooltipWrapper>
        <Row variant="left" spacing={0.5}>
          {report.channelCount > 0 && channelOpts && (
            <CountChip
              Icon={channelOpts.Icon}
              tooltip={pluralizePhrase(
                report.channelCount,
                channelOpts.sourceLabel
              )}
            >
              {report.channelCount}
            </CountChip>
          )}
          {report.messageCount > 0 && messageOpts && (
            <CountChip
              Icon={messageOpts.Icon}
              tooltip={pluralizePhrase(
                report.messageCount,
                messageOpts.sourceLabel
              )}
            >
              {report.messageCount}
            </CountChip>
          )}
          {report.impersonationCount > 0 && (
            <TooltipWrapper
              title={`Shared with ${pluralizePhrase(
                report.impersonationCount,
                "person",
                { plural: "people" }
              )}`}
            >
              <SharedReportIcon size="sm" />
            </TooltipWrapper>
          )}
        </Row>
      </Column>
    </TableCell>
  );
};

export default TopicTitleCell;
